import type { Conversation } from '../../api/chat';
import type { ChatUnreadEntry } from '../../redux/slices/chatUnread';
import { formatCallEventPreview, isCallEventMessage } from '../../utils/chat/callEventPreview';
import { isLeaveSystemMessage } from '../../utils/chat/leaveSystemMessage';

export interface ConvMeta {
  name: string;
  avatarUrl: string | null;
}

/**
 * Resolve display name + avatar for EVERY conversation (not just unread ones),
 * so the notification panel can backfill missing photos on entries — including
 * read-history rows whose avatar was never captured (e.g. created from a WS
 * frame with no sender_avatar). DMs resolve to the other member's photo, the
 * same way the sidebar / unread builder do.
 */
export function buildConvMetaMap(
  conversations: Conversation[],
  currentUserId: string
): Record<string, ConvMeta> {
  const map: Record<string, ConvMeta> = {};
  for (const c of conversations) {
    let name = c.name?.trim() || '';
    let avatarUrl: string | null = c.avatar_url ?? null;
    if (c.type === 'dm' && c.members) {
      const other = c.members.find((m) => m.user_id !== currentUserId);
      if (other) {
        if (!name) name = other.name;
        avatarUrl = (other as any).avatar_url ?? null;
      }
    }
    map[c.id] = { name: name || 'Conversation', avatarUrl };
  }
  return map;
}

export function buildChatUnreadEntries(
  conversations: Conversation[],
  currentUserId: string
): ChatUnreadEntry[] {
  const out: ChatUnreadEntry[] = [];
  for (const c of conversations) {
    if (!c.unread_count || c.unread_count <= 0) continue;
    if (isLeaveSystemMessage((c as any).last_message)) continue;

    let name = c.name?.trim() || '';
    // DMs have no conversation-level avatar — fall back to the other member's
    // photo, the same way ConversationRow resolves the DM peer avatar.
    let avatarUrl: string | null = c.avatar_url ?? null;
    if (c.type === 'dm' && c.members) {
      const other = c.members.find((m) => m.user_id !== currentUserId);
      if (other) {
        if (!name) name = other.name;
        avatarUrl = (other as any).avatar_url ?? null;
      }
    }
    const lastMessage = (c as any).last_message;
    if (!name) name = isCallEventMessage(lastMessage) ? 'Call' : 'Conversation';

    const lastContent =
      formatCallEventPreview(lastMessage, currentUserId) ??
      lastMessage?.content ??
      (c as any).last_message_content ??
      null;

    const lastMessageId =
      lastMessage?.id ??
      (c as any).last_message_id ??
      undefined;

    out.push({
      conversationId: c.id,
      count: c.unread_count,
      name,
      avatarUrl,
      lastActivity: c.last_activity,
      lastContent,
      lastMessageId,
    });
  }
  return out;
}
